'use client';

import Image from 'next/image';
import type { FC } from 'react';
import { useCollapseList } from '@/hooks/useCollapseList.hook';
import type {
  AvailableGamesImages,
  EsportsBettingFeaturesImages,
} from '@/types/bookmaker';
import MarkdownContent from '../MarkdownContent';
import { SectionImagesList } from './SectionImagesList';
import { HubSectionTitle } from './SectionTitle';

interface EsportsSectionProps {
  title: string;
  text: string;
  images: EsportsBettingFeaturesImages;
  availableGames: AvailableGamesImages;
}

export const EsportsSection: FC<EsportsSectionProps> = ({
  title,
  text,
  images,
  availableGames,
}) => {
  const {
    list: games,
    handleToggle: handleGamesToggle,
    haveHiddenItems: haveHiddenGames,
    isExpanded: isGamesExpanded,
  } = useCollapseList({
    list: availableGames?.data ?? [],
    limit: 6,
  });

  return (
    <div
      className="flex flex-col gap-3 rounded-lg bg-medium-gray p-4"
      id="esport"
    >
      <HubSectionTitle>{title}</HubSectionTitle>
      {images?.data && (
        <SectionImagesList
          imageAltPrefix="bookmaker-logo-esports-section"
          images={images.data}
        />
      )}
      <MarkdownContent>{text}</MarkdownContent>
      {games.length > 0 && (
        <div className="flex flex-col">
          <p className="mb-3 font-semibold text-2xl text-white">
            Available games
          </p>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            {games.map((image) => (
              <div
                className="flex flex-row items-center gap-4 p-3"
                key={image.id}
              >
                <div className="h-[24px] w-[24px] object-scale-down">
                  <Image
                    alt="bookmaker-logo-esports-section-2"
                    className="h-full rounded-lg"
                    height={0}
                    src={image?.attributes?.url}
                    width={24}
                  />
                </div>
                <span className="font-normal">{image.attributes.caption}</span>
              </div>
            ))}
          </div>
          {haveHiddenGames && (
            <button
              aria-expanded={games.length > 8}
              aria-label="Show more available games"
              className="flex justify-center rounded-lg border border-blue-gray border-solid px-[3.063rem] py-3 max-md:flex-1"
              onClick={() => handleGamesToggle()}
              type="button"
            >
              <p
                className="mb-0 font-semibold text-blue-gray text-sm"
                style={{ marginBottom: '0px' }}
              >
                {isGamesExpanded
                  ? 'Show less available games'
                  : 'More available games'}
              </p>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
